import { Box, Input, Text } from '@betty/moonlight';
import { BottomSheet } from '@betty/navigation';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import HistoryList from '../HistoryList/HistoryList';
import { useDispatch } from '@betty/toolkit/state';
import { formatDate } from '@betty/toolkit/utils';
import { useTranslation } from '@betty/i18n';
import useHistoryOptions from '../../hooks/useHistoryOptions';
import useHistory from '../../hooks/useHistory';
import { HistoryType } from '../../state/shared/history.types';
import { HistoryProps, SelectedOption } from './History.types';
import TransactionHistoryListItem from '../HistoryListItem/TransactionHistoryListItem';
import SessionHistoryListItem from '../HistoryListItem/SessionHistoryListItem';
import { TransactionItem } from '../../state/transactionHistory/transactionHistory.types';
import { SessionItem } from '../../state/sessionHistory/sessionHistory.types';
import GamingHistoryListItem from '../HistoryListItem/GamingHistoryListItem';
import { GamingItem } from '../../state/gamingHistory/gamingHistory.types';
import { CoinsGamingItem } from '../../state/coinsGamingTransactionHistory/coinsGamingTransactionHistory.types';
import VerifyCard from '../VerificationCard/VerifyCard';
import PendingVerificationCard from '../VerificationCard/PendingVerificationCard';
import { Pressable } from 'react-native';

function History({ type }: HistoryProps) {
  const dispatch = useDispatch();
  const { options, defaultOption } = useHistoryOptions();
  const [selected, setSelected] = useState(defaultOption);
  const {
    items,
    hasNextPage,
    pageNumber,
    lastTimestamp,
    isFetching,
    fetchHistory,
    dataState,
    resetLastTimestamp,
    hasPaymentMethodsAwaitingDocumentReview,
    hasPaymentMethodsAwaitingDocumentVerification,
  } = useHistory(selected.timestamp, type);
  const [t] = useTranslation('account');

  useEffect(() => {
    return () => {
      dispatch(resetLastTimestamp());
    };
  }, [dispatch, resetLastTimestamp]);

  const { renderItem, keyExtractor } = useMemo(() => {
    switch (type) {
      case HistoryType.Transactions:
        return {
          renderItem: ({ item }: { item: TransactionItem }) => (
            <TransactionHistoryListItem
              selectedDate={selected.timestamp}
              {...item}
            />
          ),
          keyExtractor: (item: TransactionItem) => `${item.transactionId}`,
        };
      case HistoryType.Sessions:
        return {
          renderItem: ({ item }: { item: SessionItem }) => (
            <SessionHistoryListItem {...item} />
          ),
          keyExtractor: (item: SessionItem) => `${item.id}`,
        };
      case HistoryType.Gaming:
        return {
          renderItem: ({ item }: { item: GamingItem }) => (
            <GamingHistoryListItem type={type} {...item} />
          ),
          keyExtractor: (item: GamingItem) => `${item.roundId}`,
        };
      case HistoryType.CoinGaming:
        return {
          renderItem: ({ item }: { item: CoinsGamingItem }) => (
            <GamingHistoryListItem type={type} {...item} />
          ),
          keyExtractor: (item: CoinsGamingItem) => `${item.roundId}`,
        };
    }
  }, [selected.timestamp, type]);

  const fetchMoreItems = useCallback(() => {
    if (hasNextPage && pageNumber && lastTimestamp && dataState !== 'pending') {
      dispatch(
        fetchHistory({
          pageNumber: pageNumber + 1,
          startDate: lastTimestamp,
          replace: false,
        }),
      );
    }
  }, [
    dataState,
    hasNextPage,
    pageNumber,
    lastTimestamp,
    dispatch,
    fetchHistory,
  ]);

  const onSelect = useCallback(
    (option: SelectedOption) => {
      setSelected(option);
    },
    [setSelected],
  );
  const startDate = useMemo(
    () => formatDate(selected.timestamp, 'MM/DD/YYYY'),
    [selected],
  );
  const endDate = useMemo(() => formatDate(new Date()), []);

  const onOccupationPressIn = useCallback(() => {
    BottomSheet.open({
      namespace: 'common',
      type: 'Select',
      data: {
        selectableOptions: options,
        maxHeight: '100%',
        buttonNS: 'account',
        buttonKey: 'historySaveButton',
        onSelect,
      },
    });
  }, [options, onSelect]);

  return (
    <Box flex={1} gap="space-600" paddingTop="space-600">
      <Box gap="space-600" paddingHorizontal="space-600">
        <Pressable onPress={onOccupationPressIn}>
          <Input
            editable={false}
            value={selected.text}
            enterKeyHint="next"
            onPressIn={onOccupationPressIn}
          />
        </Pressable>
        <Text variant="body-m-semi" gradient="darkSurfaceAccent">
          {t('historyDateRange', {
            startDate,
            endDate,
            interpolation: { escapeValue: false },
          })}
        </Text>
        {hasPaymentMethodsAwaitingDocumentVerification ? (
          <VerifyCard selectedTimeStamp={selected.timestamp} />
        ) : null}
        {hasPaymentMethodsAwaitingDocumentReview ? (
          <PendingVerificationCard />
        ) : null}
      </Box>
      <HistoryList
        items={items}
        onEndReached={fetchMoreItems}
        isFetching={isFetching}
        pageNumber={pageNumber}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        skeletonItemHeight={100}
      />
    </Box>
  );
}

export default memo(History);
